import { ApiPost, ApiPut, ApiGet } from './Common.ts'
import { GetOrderEntryConnectorUrl, GetWebConnectorExtensionsUrl,
          GetWebConnectorUrl
 } from '@/stores/SettingsStore.ts'
import type { IBatchCurrentGetResponse } from '@/types/BatchTypes.d.ts'

/**
 * Updates the business name for a given BEE number
 * @param beeNumber The BEE number of the business entity
 * @param businessName The new business name to set
 * @returns Promise with the update response
 */
export async function BusinessNameUpdate(beeNumber: number, businessName: string) {
  const url = await GetOrderEntryConnectorUrl('BeeEntityBusinessNameUpdateConnector')

  return ApiPost(url, {
    BeeNumber: beeNumber,
    BusinessName: businessName
  })
}

export async function BeeEntityWebAccountVerifyEmail(beeNumberToken: string) {
  const url = await GetOrderEntryConnectorUrl('BeeEntityWebAccountVerifyEmailConnector')

  return ApiPost(url, {
    BeeNumberToken: beeNumberToken
  })
}

export async function BeeEntityWebAccountDefaultGet(beeNumberToken: string) {
  const url = await GetOrderEntryConnectorUrl('BeeEntityWebAccountDefaultGet')

  return ApiGet(url, {
    BeeNumberToken: beeNumberToken
  })
}

/**
 * Updates the French contribution information for a consultant during New User Wizard
 * @param frenchContributionData The French contribution data to update
 * @returns Promise with the update response
 */
export async function FrenchContributionNUWUpdate(FrenchContributionUpdate: {
  BeeNumber: number;
  BirthCity: string;
  BirthCountry: string;
  FrenchCitizen: boolean;
  AuthorizedToWork: boolean;
  EmployeeStatus: string;
  SSN: string;
  SIRET: string;
}) {
  const url = await GetWebConnectorExtensionsUrl('FrenchContributionSave')
  console.log("webConnectorExtensionsUrl",url);
  return ApiPost(url, FrenchContributionUpdate)
}

/**
 * Creates or updates VAT/Tax exemption information for a consultant during New User Wizard
 * @param taxExemptData The tax exemption data to create
 * @returns Promise with the creation response
 */
export async function TaxExemptNUWCreate(taxExemptData: {
  BeeNumber: string;
  TaxExemptCode: string;
  CodeIdExtendedTaxId: string;
}) {
  const url = await GetOrderEntryConnectorUrl('TaxExemptNUWCreate')
  return ApiPost(url, taxExemptData)
}

/**
 * Updates the SFA User record to mark the New User Wizard as completed
 * @param sfaUserData The SFA User data to update
 * @returns Promise with the update response
 */
export async function SfaUserNUWUpdate(sfaUserData: {
  BeeNumber: number;
  WizardHasBeenRun: boolean;
}) {
  console.log("SfaUserNUWUpdate", sfaUserData);
  const url = await GetOrderEntryConnectorUrl('SfaUserNUWUpdate')
  return ApiPost(url, sfaUserData)
}

/**
 * Gets tax exempt information for a consultant
 * @param beeNumber The BEE number to get tax exempt information for
 * @returns Promise with the tax exempt information
 */
export async function PwsTaxExemptGet(beeNumber: string) {
  const url = await GetOrderEntryConnectorUrl('PwsTaxExemptGet')
  return ApiGet(url, { BeeNumber: beeNumber })
}

/**
 * Gets historical batch data for business metrics
 * @param take Number of records to return
 * @param skip Number of records to skip
 * @returns Promise with the batch history data
 */
export async function PWSHstBatchGetList(take: number = 10, skip: number = 0) {
  const url = await GetWebConnectorUrl('PWSHstBatchGetList')

  const responseFilter = {
    OrderBy: [
      {
        Field: 'AppPeriodDateIndex',
        Order: 'Desc'
      }
    ],
    Skip: skip,
    Take: take
  }

  return ApiPost(url, { ResponseFilter: responseFilter })
}

/**
 * Gets current historical batch for business metrics
 * @returns Promise with current and previous batch numbers
 */
export async function PwsHstBatchCurrentGet(): Promise<IBatchCurrentGetResponse> {
  const url = await GetWebConnectorUrl('PwsHstBatchCurrentGet')
  return ApiPost(url, {})
}

/**
 * Gets business details for specific metrics and batch
 * @param columnNames List of column names to retrieve
 * @param batchNumber The batch number to get details for
 * @param beeNumber Optional bee number for impersonation
 * @returns Promise with the business details data
 */
export async function PwsBusinessDetailsGet(columnNames: string[], batchNumber: string, beeNumber?: number) {
  const url = await GetWebConnectorUrl('PwsBusinessDetailsGet')
  return ApiPost(url, {
    FieldName: columnNames,
    BatchNumber: batchNumber,
    BeeNumber: beeNumber
  })
}
